// Phase 53 (2026-08-08) — Transaction Pipeline panel for the Approvals page.
//
// Two internal tabs:
//   • To Verify — senior_accountant / admin. Approve / Send Back / Reject.
//   • To Post   — admin only. Post to GL (creates the JE via the existing
//                 accounting service; the GL itself is untouched).
//
// Renders a compact row per pending tx with expandable detail (Dr/Cr
// proposed lines, attachments count, reviewer_comments, stage_history
// timeline). Actions call the new /api/transactions/{id}/pipeline/*
// endpoints. Everything else (reports, dashboard, GL) is untouched.

import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, CornerUpLeft, Send, Loader2, ChevronDown,
  ChevronRight, Paperclip, ArrowRightCircle,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";
import { fmtUSD, typeLabel } from "@/lib/tx";

const VERIFIER_ROLES = [
  ROLES.SYSTEM_ADMIN,
  ROLES.SALIHI_SENIOR_ACCOUNTANT,
  ROLES.SALIHI_FINANCE_MANAGER,
];

function fmtDate(s) {
  if (!s) return "—";
  return s.length > 10 ? new Date(s).toLocaleString() : s;
}

function StageBadge({ stage }) {
  const map = {
    draft:          { cls: "bg-slate-100 text-slate-700 border-slate-300",  label: "Draft" },
    pending_verify: { cls: "bg-amber-100 text-amber-800 border-amber-300",  label: "Pending Verify" },
    sent_back:      { cls: "bg-orange-100 text-orange-800 border-orange-300",label: "Sent Back" },
    pending_post:   { cls: "bg-sky-100 text-sky-800 border-sky-300",        label: "Pending Post" },
    posted:         { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Posted" },
    rejected:       { cls: "bg-red-100 text-red-800 border-red-300",        label: "Rejected" },
  };
  const m = map[stage] || map.draft;
  return (
    <Badge variant="outline" className={`${m.cls} text-[10px] font-medium border`}
           data-testid={`stage-badge-${stage}`}>
      {m.label}
    </Badge>
  );
}

function StageHistoryTimeline({ history = [], comments = [] }) {
  if (!history.length && !comments.length) {
    return <p className="text-xs text-slate-400 italic">No history yet.</p>;
  }
  return (
    <ol className="space-y-2" data-testid="stage-history-timeline">
      {history.map((h, i) => (
        <li key={`h-${i}`} className="rounded-md bg-slate-50 p-2 text-xs">
          <div className="flex items-center gap-2">
            <ArrowRightCircle className="h-3 w-3 text-sky-600" />
            <span className="font-medium capitalize">{h.action || "transition"}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-600">
              {h.from_stage || "—"} → <span className="font-semibold">{h.to_stage}</span>
            </span>
          </div>
          <div className="text-slate-500 pl-5">
            {fmtDate(h.timestamp)} — {h.actor_email} ({h.actor_role})
          </div>
          {h.comment && (
            <div className="pl-5 mt-1 italic text-slate-700">&ldquo;{h.comment}&rdquo;</div>
          )}
        </li>
      ))}
    </ol>
  );
}

function TxRow({ tx, tab, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showSendBack, setShowSendBack] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [expenseAcc, setExpenseAcc] = useState("");
  const [bankAcc, setBankAcc] = useState("");

  const doAction = async (endpoint, body, successMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/transactions/${tx.id}/pipeline/${endpoint}`,
        body || {},
      );
      toast.success(successMsg + (data?.journal_entry_id ? ` (JE ${data.journal_entry_id.slice(0, 8)})` : ""));
      setShowSendBack(false); setShowReject(false); setShowPost(false);
      setComment(""); setReason("");
      if (onChange) await onChange();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || formatApiError(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-2" data-testid={`tx-pipeline-row-${tx.id}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-slate-400 hover:text-slate-700"
            data-testid={`tx-pipeline-expand-${tx.id}`}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <div className="flex-1 grid grid-cols-6 gap-2 items-center text-sm">
            <div className="text-slate-700">{fmtDate(tx.transaction_date).slice(0, 10)}</div>
            <div className="text-slate-700 truncate" title={tx.description}>
              {tx.description || "—"}
            </div>
            <div className="text-slate-600 text-xs">{tx.counterparty_name || "—"}</div>
            <div className="text-right font-semibold tabular-nums">
              {fmtUSD(tx.amount)}
            </div>
            <div className="text-xs">
              {typeLabel(tx.transaction_type) || tx.transaction_type}
            </div>
            <div className="flex items-center gap-1 justify-end">
              <StageBadge stage={tx.stage} />
              {(tx.attachments?.length || 0) > 0 && (
                <span className="text-xs text-sky-700">
                  <Paperclip className="inline h-3 w-3" />
                  {tx.attachments.length}
                </span>
              )}
            </div>
          </div>
        </div>

        {open && (
          <div className="mt-3 grid gap-3 md:grid-cols-2 border-t pt-3">
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-slate-500 text-xs uppercase">Creator</span>
                <div className="text-slate-700">{tx.created_by_email || "—"}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs uppercase">Payment method</span>
                <div className="text-slate-700">{tx.payment_method || "—"}</div>
              </div>
              <div>
                <span className="text-slate-500 text-xs uppercase">Reference</span>
                <div className="text-slate-700">{tx.reference_number || "—"}</div>
              </div>
              {(tx.reviewer_comments || []).length > 0 && (
                <div>
                  <span className="text-slate-500 text-xs uppercase">Reviewer comments</span>
                  <ul className="space-y-1 mt-1">
                    {tx.reviewer_comments.map((c, i) => (
                      <li key={i} className="rounded bg-amber-50 border border-amber-200 p-2 text-xs">
                        <div className="font-semibold text-amber-900">{c.author_email}</div>
                        <div className="text-slate-500">{fmtDate(c.timestamp)}</div>
                        <div className="text-slate-700 mt-0.5">{c.comment}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {tx.journal_entry_id && (
                <div>
                  <span className="text-slate-500 text-xs uppercase">Journal Entry</span>
                  <div>
                    <a href="/bookkeeping" className="text-sky-700 hover:underline font-mono text-xs">
                      {tx.journal_entry_id}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs uppercase text-slate-500 mb-2">Stage history</p>
              <StageHistoryTimeline history={tx.stage_history} comments={tx.reviewer_comments} />
            </div>

            {/* Phase 53 fix (2026-08-08) — Proposed JE preview. Rendered
                on BOTH the To Verify and To Post expand rows so the
                verifier/admin sees the exact Dr/Cr that will hit the
                GL BEFORE clicking Post. The `proposed_je` payload is
                already embedded on each row by the list endpoint (see
                phase53_transaction_pipeline.py). */}
            <div className="md:col-span-2 pt-1" data-testid={`proposed-je-${tx.id}`}>
              <p className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-2">
                Proposed Journal Entry
                {tx.proposed_je && tx.proposed_je.balanced && (
                  <span className="inline-flex items-center rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                        data-testid={`proposed-je-balanced-${tx.id}`}>
                    Balanced ✓
                  </span>
                )}
                {tx.proposed_je && !tx.proposed_je.balanced && (
                  <span className="inline-flex items-center rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-800"
                        data-testid={`proposed-je-unbalanced-${tx.id}`}>
                    Unbalanced ✗
                  </span>
                )}
              </p>
              {tx.proposed_je && (tx.proposed_je.lines || []).length > 0 ? (
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-semibold">Account Code</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Account Name</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Debit</th>
                        <th className="text-right px-2 py-1.5 font-semibold">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tx.proposed_je.lines.map((l, i) => (
                        <tr key={i} className={i % 2 === 1 ? "bg-slate-50/60" : ""}>
                          <td className="px-2 py-1.5 font-mono">{l.account_code}</td>
                          <td className="px-2 py-1.5 text-slate-700">{l.account_name}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {Number(l.debit) > 0 ? fmtUSD(l.debit) : ""}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {Number(l.credit) > 0 ? fmtUSD(l.credit) : ""}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-semibold border-t">
                        <td colSpan={2} className="px-2 py-1.5 text-right">Totals</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {fmtUSD(tx.proposed_je.total_debit)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {fmtUSD(tx.proposed_je.total_credit)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-xs italic text-slate-400 border border-dashed rounded-md p-2"
                     data-testid={`proposed-je-empty-${tx.id}`}>
                  Proposed lines could not be derived yet (missing counterparty or
                  posting-account overrides). The admin can supply the account codes
                  in the Post-to-GL modal.
                </div>
              )}
            </div>

            {/* Action row */}
            {tab === "verify" && (
              <div className="md:col-span-2 flex flex-wrap gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={busy}
                  onClick={() => doAction("verify", { comment }, "Transaction verified.")}
                  data-testid={`tx-pipeline-verify-${tx.id}`}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  Approve
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  onClick={() => setShowSendBack(true)}
                  data-testid={`tx-pipeline-send-back-${tx.id}`}
                >
                  <CornerUpLeft className="h-3.5 w-3.5 mr-1" /> Send Back
                </Button>
                <Button
                  size="sm" variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setShowReject(true)}
                  data-testid={`tx-pipeline-reject-${tx.id}`}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </div>
            )}
            {tab === "post" && (
              <div className="md:col-span-2 flex flex-wrap gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  disabled={busy}
                  onClick={() => setShowPost(true)}
                  data-testid={`tx-pipeline-post-${tx.id}`}
                >
                  <Send className="h-3.5 w-3.5 mr-1" /> Post to GL
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Send-back modal */}
        <Dialog open={showSendBack} onOpenChange={setShowSendBack}>
          <DialogContent>
            <DialogHeader><DialogTitle>Send back to creator</DialogTitle></DialogHeader>
            <Label className="text-xs">Comment (min 5 chars — sent to the creator)</Label>
            <Textarea
              rows={3} value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Please attach the supplier invoice."
              data-testid="send-back-comment"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSendBack(false)}>Cancel</Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={busy || comment.trim().length < 5}
                onClick={() => doAction("send-back", { comment }, "Sent back to creator.")}
                data-testid="send-back-confirm"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Send Back
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject modal */}
        <Dialog open={showReject} onOpenChange={setShowReject}>
          <DialogContent>
            <DialogHeader><DialogTitle>Reject transaction</DialogTitle></DialogHeader>
            <Label className="text-xs">Reason (min 5 chars — recorded in audit log)</Label>
            <Textarea
              rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate of TX-2026-0042"
              data-testid="reject-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={busy || reason.trim().length < 5}
                onClick={() => doAction("reject", { reason }, "Transaction rejected.")}
                data-testid="reject-confirm"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Post-to-GL confirm modal */}
        <Dialog open={showPost} onOpenChange={setShowPost}>
          <DialogContent>
            <DialogHeader><DialogTitle>Post transaction to GL</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              This creates a Journal Entry in the General Ledger and cannot be
              undone. Continue?
            </p>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-xs">Expense/GL account code (optional)</Label>
                <Input value={expenseAcc}
                       onChange={(e) => setExpenseAcc(e.target.value)}
                       placeholder="e.g. 5020"
                       data-testid="post-expense-account" />
              </div>
              <div>
                <Label className="text-xs">Bank account code (optional)</Label>
                <Input value={bankAcc}
                       onChange={(e) => setBankAcc(e.target.value)}
                       placeholder="e.g. 1011"
                       data-testid="post-bank-account" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPost(false)}>Cancel</Button>
              <Button
                className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                disabled={busy}
                onClick={() => doAction("post", {
                  ...(expenseAcc ? { posting_expense_account: expenseAcc } : {}),
                  ...(bankAcc ? { posting_bank_account: bankAcc } : {}),
                }, "Posted to GL.")}
                data-testid="post-confirm"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Post to GL
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function TxPipelinePanel() {
  const { user } = useAuth();
  const role = user?.role;
  const canVerify = VERIFIER_ROLES.includes(role) || user?.full_access === true;
  const canPost = role === ROLES.SYSTEM_ADMIN || user?.full_access === true;
  const [tab, setTab] = useState(canVerify ? "verify" : (canPost ? "post" : "verify"));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const stage = tab === "verify" ? "pending_verify" : "pending_post";
      const { data } = await api.get(
        `/transactions/pipeline/list?stage=${stage}&page_size=100`,
      );
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  if (!canVerify && !canPost) {
    return (
      <Card><CardContent className="py-10 text-center text-slate-400">
        Your role does not have access to the transaction pipeline queue.
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="tx-pipeline-panel">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {canVerify && (
            <TabsTrigger value="verify" data-testid="tab-to-verify">
              To Verify
            </TabsTrigger>
          )}
          {canPost && (
            <TabsTrigger value="post" data-testid="tab-to-post">
              To Post
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin h-6 w-6 text-[#4FA3D1]" />
            </div>
          ) : items.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-400"
                                data-testid="pipeline-empty">
              No transactions {tab === "verify" ? "awaiting verification" : "awaiting posting"}.
            </CardContent></Card>
          ) : (
            <>
              <div className="grid grid-cols-6 gap-2 text-xs uppercase text-slate-500 px-8 pb-1">
                <div>Date</div><div>Description</div><div>Counterparty</div>
                <div className="text-right">Amount</div><div>Type</div>
                <div className="text-right">Stage / Attach</div>
              </div>
              {items.map((tx) => (
                <TxRow key={tx.id} tx={tx} tab={tab} onChange={load} />
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
